import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaRedo,
  FaChartLine,
  FaPrint,
  FaFileExcel,
} from "react-icons/fa";
import {
  getTasks,
  deleteTask,
  updateTaskProgress,
} from "../../../../redux/Slices/TasksSlice";
import { getEmployees } from "../../../../redux/Slices/authSlice";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
  chunkArray,
} from "../../../../shared/utils.js/utils";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import {
  saveTasksCache,
  getTasksCache,
  generateTasksCacheKey,
  generateGeneralTasksCacheKey,
  getAllTasksCacheForQuery,
  clearOldTasksCache,
  isOnline,
  saveDropdownCache,
  getDropdownCache,
  generateDropdownCacheKey,
  removeTaskFromCache,
  getAllDropdownCacheForQuery,
} from "../../../../shared/utils.js/offlineQueue";
import { makeOfflineRequest } from "../../../../shared/utils.js/offlineRequestManager";

const normalizeListResponse = payload => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

// tasks component
const Tasks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [perPageInput, setPerPageInput] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");
  const [projectId, setProjectId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [taskForProgress, setTaskForProgress] = useState(null);
  const [progressValue, setProgressValue] = useState("");
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [taskForPrint, setTaskForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [previewTask, setPreviewTask] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0); // Start with zoom at 100%
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const previewContainerRef = useRef(null);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [attachmentsForModal, setAttachmentsForModal] = useState([]);
  const [attachmentsTaskName, setAttachmentsTaskName] = useState("");
  const publicUrl = (
    process.env.PUBLIC_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
  ).replace(/\/$/, "");
  const headerLogo = `${publicUrl}/ACE.png`;

  // Project dropdown
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const projectSearchInputRef = useRef(null);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);

  // Employee dropdown
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);
  const [isEmployeesLoading, setIsEmployeesLoading] = useState(false);

  // Refs to track previous values for optimized fetching
  const lastProjectSearchRef = useRef("");
  const lastProjectPageRef = useRef(1);
  const lastEmployeeSearchRef = useRef("");
  const lastEmployeePageRef = useRef(1);

  // Prevent repeated offline toasts for dropdowns
  const projectsOfflineToastShownRef = useRef(false);
  const employeesOfflineToastShownRef = useRef(false);
  const tasksOfflineToastShownRef = useRef(false);

  const paginationInfo = useMemo(() => {
    if (!pagination) return { current_page: currentPage, last_page: 1 };
    return {
      current_page: pagination.current_page || pagination.page || currentPage,
      last_page: pagination.last_page || pagination.total_pages || 1,
    };
  }, [pagination, currentPage]);

  const handleApplyPerPage = () => {
    const total = pagination?.total || tasks.length || 0;
    const maxValue = total > 0 ? total : 1000;
    const safeValue = Math.max(1, Math.min(maxValue, perPageInput));
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  const fetchTasks = useCallback(
    async (
      page = currentPage,
      search = searchTerm,
      project = projectId,
      status = statusFilter,
      employee = employeeId,
      sort = sortBy,
      order = sortOrder,
    ) => {
      try {
        setIsLoading(true);

        // Generate cache key from query parameters
        const queryParams = {
          page,
          per_page: perPage,
          search: search || undefined,
          project_id: project || undefined,
          status: status || undefined,
          employee_id: employee || undefined,
          sort_by: sort || undefined,
          sort_order: order || undefined,
        };
        const cacheKey = generateTasksCacheKey(queryParams);

        // Step 1: If offline, use all cached data for pagination support
        if (!isOnline()) {
          // Offline: use all cached data for this query (all pages) to support offline pagination
          const allCachedTasks = await getAllTasksCacheForQuery({
            per_page: perPage,
            search: search || undefined,
            project_id: project || undefined,
            status: status || undefined,
            employee_id: employee || undefined,
          });

          if (Array.isArray(allCachedTasks) && allCachedTasks.length > 0) {
            const total = allCachedTasks.length;
            const lastPage = Math.max(1, Math.ceil(total / perPage));
            const safePage = Math.min(page, lastPage);
            const startIndex = (safePage - 1) * perPage;
            const pageItems = allCachedTasks.slice(
              startIndex,
              startIndex + perPage,
            );

            setTasks(pageItems);
            setPagination({
              current_page: safePage,
              last_page: lastPage,
              per_page: perPage,
              total,
            });

            if (!tasksOfflineToastShownRef.current) {
              tasksOfflineToastShownRef.current = true;
              toast.info(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت. يتم عرض المهام المحفوظة محلياً."
                  : "No internet connection. Showing locally saved tasks.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            setIsLoading(false);
            return; // Exit early when offline
          } else {
            if (!tasksOfflineToastShownRef.current) {
              tasksOfflineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة محلياً."
                  : "No internet connection and no locally saved data available.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            setTasks([]);
            setPagination(null);
            setIsLoading(false);
            return; // Exit early when offline and no cache
          }
        }

        // Step 2: Try to load from IndexedDB first (for quick display when online)
        let cachedData = null;
        try {
          cachedData = await getTasksCache(cacheKey);

          if (
            cachedData &&
            cachedData.tasks &&
            Array.isArray(cachedData.tasks) &&
            cachedData.tasks.length > 0
          ) {
            setTasks(cachedData.tasks);
            setPagination(cachedData.pagination);
            // Don't set isLoading to false here, continue to API sync
          } else {
          }
        } catch (cacheError) {
          console.error("Error loading from cache:", cacheError);
        }

        // Step 3: Try to fetch from API (for sync/update)
        if (isOnline()) {
          try {
            const payload = await dispatch(
              getTasks({
                page,
                per_page: perPage,
                search: search || undefined,
                project_id: project || undefined,
                status: status || undefined,
                employee_id: employee || undefined,
                sort_by: sort || undefined,
                sort_order: order || undefined,
              }),
            ).unwrap();
            const { items, pagination: paginationMeta } =
              normalizeListResponse(payload);

            // Update IndexedDB cache with fresh data for this specific page
            try {
              await saveTasksCache(cacheKey, items, paginationMeta);

              // Also save to general cache (without page number) for fallback
              const generalCacheKey = generateGeneralTasksCacheKey(queryParams);
              // For general cache, we'll save all items we have so far
              // Get existing general cache and merge
              const existingGeneralCache = await getTasksCache(generalCacheKey);
              let allTasksForQuery = existingGeneralCache?.tasks || [];

              // Merge with new items (avoid duplicates)
              items.forEach(newTask => {
                const exists = allTasksForQuery.find(t => t.id === newTask.id);
                if (!exists) {
                  allTasksForQuery.push(newTask);
                } else {
                  // Update existing task
                  const index = allTasksForQuery.findIndex(
                    t => t.id === newTask.id,
                  );
                  allTasksForQuery[index] = newTask;
                }
              });

              // Save general cache
              await saveTasksCache(generalCacheKey, allTasksForQuery, {
                ...paginationMeta,
                current_page: 1, // General cache doesn't have a specific page
                total: allTasksForQuery.length,
              });
            } catch (saveError) {
              console.error("Error saving to cache:", saveError);
            }

            // Update UI with fresh data
            setTasks(items);
            setPagination(paginationMeta);
          } catch (apiError) {
            console.error(
              "API fetch failed, trying cached data fallback:",
              apiError,
            );

            // Try to fall back to all cached tasks (all pages for same query) for offline-like pagination
            const allCachedTasks = await getAllTasksCacheForQuery({
              per_page: perPage,
              search: search || undefined,
              project_id: project || undefined,
              status: status || undefined,
              employee_id: employee || undefined,
            });

            if (Array.isArray(allCachedTasks) && allCachedTasks.length > 0) {
              const total = allCachedTasks.length;
              const lastPage = Math.max(1, Math.ceil(total / perPage));
              const safePage = Math.min(page, lastPage);
              const startIndex = (safePage - 1) * perPage;
              const pageItems = allCachedTasks.slice(
                startIndex,
                startIndex + perPage,
              );

              setTasks(pageItems);
              setPagination({
                current_page: safePage,
                last_page: lastPage,
                per_page: perPage,
                total,
              });

              if (!tasksOfflineToastShownRef.current) {
                tasksOfflineToastShownRef.current = true;
                toast.warning(
                  isRTL
                    ? "لا يمكن الاتصال بالخادم. يتم عرض المهام المحفوظة محلياً."
                    : "Cannot connect to server. Showing locally saved tasks.",
                  { rtl: isRTL, autoClose: 3000 },
                );
              }
            } else if (
              !cachedData ||
              !cachedData.tasks ||
              !Array.isArray(cachedData.tasks) ||
              cachedData.tasks.length === 0
            ) {
              // No cached data available, show error
              const errorMessage =
                typeof apiError === "string"
                  ? apiError
                  : apiError?.message ||
                    apiError?.response?.data?.message ||
                    t("tasks.messages.fetchError");
              toast.error(errorMessage, { rtl: i18n.language === "ar" });
              // Set empty state
              setTasks([]);
              setPagination(null);
            } else {
              // We have page-specific cached data, show warning but keep using it
              if (!tasksOfflineToastShownRef.current) {
                tasksOfflineToastShownRef.current = true;
                toast.warning(
                  isRTL
                    ? "لا يمكن الاتصال بالخادم. يتم عرض البيانات المحفوظة محلياً."
                    : "Cannot connect to server. Showing locally saved data.",
                  { rtl: isRTL, autoClose: 3000 },
                );
              }
            }
          }
        }
      } catch (error) {
        console.error("Error in fetchTasks:", error);
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message ||
              error?.response?.data?.message ||
              t("tasks.messages.fetchError");
        toast.error(errorMessage, { rtl: i18n.language === "ar" });
        setTasks([]);
        setPagination(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      currentPage,
      searchTerm,
      projectId,
      statusFilter,
      employeeId,
      sortBy,
      sortOrder,
      t,
      i18n,
      perPage,
      isRTL,
    ],
  );

  const getTaskAttachments = task => {
    if (!task) return [];
    if (Array.isArray(task.files)) return task.files;
    if (Array.isArray(task.attachments)) return task.attachments;
    if (Array.isArray(task.documents)) return task.documents;
    return [];
  };

  const fetchProjects = useCallback(
    async (page, search) => {
      try {
        setIsProjectsLoading(true);
        const queryPage = page || projectPage || 1;
        const querySearch = search !== undefined ? search : projectSearchTerm;

        // Generate cache key
        const cacheKey = generateDropdownCacheKey("tasks_projects", {
          page: queryPage,
          per_page: 10,
          search: querySearch || "",
        });

        // Step 1: If offline, use all cached data for pagination support
        if (!isOnline()) {
          const allCachedItems = await getAllDropdownCacheForQuery(
            "tasks_projects",
            {
              per_page: 10,
              search: querySearch || "",
            },
          );

          if (Array.isArray(allCachedItems) && allCachedItems.length > 0) {
            const total = allCachedItems.length;
            const perPage = 10;
            const lastPage = Math.max(1, Math.ceil(total / perPage));
            const safePage = Math.min(queryPage, lastPage);
            const startIndex = (safePage - 1) * perPage;
            const pageItems = allCachedItems.slice(
              startIndex,
              startIndex + perPage,
            );

            setProjectOptions(pageItems);
            setProjectPagination({
              current_page: safePage,
              last_page: lastPage,
              per_page: perPage,
              total,
            });

            if (!projectsOfflineToastShownRef.current) {
              projectsOfflineToastShownRef.current = true;
              toast.info(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت. يتم عرض بيانات المشاريع المحفوظة محلياً."
                  : "No internet connection. Showing locally saved projects.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            return;
          } else {
            if (!projectsOfflineToastShownRef.current) {
              projectsOfflineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات مشاريع محفوظة."
                  : "No internet connection and no locally saved projects.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            setProjectOptions([]);
            setProjectPagination(null);
            return;
          }
        }

        // Step 2: Try to load from IndexedDB first (Source of Truth)
        let cachedData = null;
        try {
          cachedData = await getDropdownCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.items)) {
            setProjectOptions(cachedData.items);
            setProjectPagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error(
            "Tasks: Error loading projects from cache:",
            cacheError,
          );
        }

        // Step 3: Try to fetch from API (for sync/update)
        if (isOnline()) {
          try {
            const token = getToken();
            if (!token) throw new Error(t("tasks.messages.noAuthToken"));
            const currentLang = i18n.language || "ar";
            const response = await axios.get(`${BASE_URL}/projects`, {
              params: {
                page: queryPage,
                per_page: 10,
                search: querySearch || undefined,
              },
              headers: {
                Authorization: `Bearer ${token}`,
                "Accept-Language": currentLang,
              },
            });
            const { items, pagination: paginationMeta } = normalizeListResponse(
              response.data,
            );

            // Update IndexedDB cache with fresh data
            try {
              await saveDropdownCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error(
                "Tasks: Error saving projects to cache:",
                saveError,
              );
            }

            setProjectOptions(items);
            setProjectPagination(paginationMeta);
          } catch (apiError) {
            if (
              !cachedData ||
              !Array.isArray(cachedData.items) ||
              cachedData.items.length === 0
            ) {
              toast.error(
                apiError?.response?.data?.message ||
                  t("tasks.messages.fetchProjectsError"),
                { rtl: isRTL },
              );
              setProjectOptions([]);
              setProjectPagination(null);
            } else if (!projectsOfflineToastShownRef.current) {
              projectsOfflineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يمكن الاتصال بالخادم. يتم عرض بيانات المشاريع المحفوظة محلياً."
                  : "Cannot connect to server. Showing locally saved projects.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
          }
        }
      } catch (error) {
        console.error("Tasks: Error in fetchProjects:", error);
        toast.error(
          error?.response?.data?.message ||
            t("tasks.messages.fetchProjectsError"),
          { rtl: isRTL },
        );
        setProjectOptions([]);
        setProjectPagination(null);
      } finally {
        setIsProjectsLoading(false);
      }
    },
    [t, i18n, isRTL],
  );

  const fetchEmployees = useCallback(
    async (page, search) => {
      try {
        setIsEmployeesLoading(true);
        const queryPage = page || employeePage || 1;
        const querySearch = search !== undefined ? search : employeeSearchTerm;

        const cacheKey = generateDropdownCacheKey("tasks_employees", {
          page: queryPage,
          per_page: 10,
          search: querySearch || "",
          language: i18n.language,
        });

        // Step 1: If offline, use all cached data for pagination support
        if (!isOnline()) {
          const allCachedItems = await getAllDropdownCacheForQuery(
            "tasks_employees",
            {
              per_page: 10,
              search: querySearch || "",
            },
          );

          if (Array.isArray(allCachedItems) && allCachedItems.length > 0) {
            const total = allCachedItems.length;
            const perPage = 10;
            const lastPage = Math.max(1, Math.ceil(total / perPage));
            const safePage = Math.min(queryPage, lastPage);
            const startIndex = (safePage - 1) * perPage;
            const pageItems = allCachedItems.slice(
              startIndex,
              startIndex + perPage,
            );

            setEmployeeOptions(pageItems);
            setEmployeePagination({
              current_page: safePage,
              last_page: lastPage,
              per_page: perPage,
              total,
            });

            if (!employeesOfflineToastShownRef.current) {
              employeesOfflineToastShownRef.current = true;
              toast.info(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت. يتم عرض بيانات الموظفين المحفوظة محلياً."
                  : "No internet connection. Showing locally saved employees.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            return;
          } else {
            if (!employeesOfflineToastShownRef.current) {
              employeesOfflineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات موظفين محفوظة."
                  : "No internet connection and no locally saved employees.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            setEmployeeOptions([]);
            setEmployeePagination(null);
            return;
          }
        }

        // Step 2: Try to load from IndexedDB first (Source of Truth)
        let cachedData = null;
        try {
          cachedData = await getDropdownCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.items)) {
            setEmployeeOptions(cachedData.items);
            setEmployeePagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error(
            "Tasks: Error loading employees from cache:",
            cacheError,
          );
        }

        // Step 3: Try to fetch from API (for sync/update)
        if (isOnline()) {
          try {
            const payload = await dispatch(
              getEmployees({
                page: queryPage,
                per_page: 10,
                search: querySearch || undefined,
                language: i18n.language,
              }),
            ).unwrap();
            const { items, pagination: paginationMeta } =
              normalizeListResponse(payload);

            try {
              await saveDropdownCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error(
                "Tasks: Error saving employees to cache:",
                saveError,
              );
            }

            setEmployeeOptions(items);
            setEmployeePagination(paginationMeta);
          } catch (apiError) {
            if (
              !cachedData ||
              !Array.isArray(cachedData.items) ||
              cachedData.items.length === 0
            ) {
              toast.error(apiError || t("tasks.messages.fetchEmployeesError"), {
                rtl: isRTL,
              });
              setEmployeeOptions([]);
              setEmployeePagination(null);
            } else if (!employeesOfflineToastShownRef.current) {
              employeesOfflineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يمكن الاتصال بالخادم. يتم عرض بيانات الموظفين المحفوظة محلياً."
                  : "Cannot connect to server. Showing locally saved employees.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
          }
        }
      } catch (error) {
        console.error("Tasks: Error in fetchEmployees:", error);
        toast.error(error || t("tasks.messages.fetchEmployeesError"), {
          rtl: isRTL,
        });
        setEmployeeOptions([]);
        setEmployeePagination(null);
      } finally {
        setIsEmployeesLoading(false);
      }
    },
    [dispatch, t, i18n, isRTL],
  );

  useEffect(() => {
    setTitle("sidenav.tasks");

    // Clear old cache on mount (once per session)
    clearOldTasksCache();

    // Listen for online event to sync data
    // We refresh once when coming back online to sync with server
    // But we also rely on offlineRequestsRetried event for more specific updates
    const handleOnline = () => {
      // Refresh once when coming back online
      // This ensures data is synced after being offline
      fetchTasks(currentPage, searchTerm, projectId, statusFilter, employeeId, sortBy, sortOrder);
    };

    const handleOfflineRequestsRetried = event => {
      // Only refresh if there were actual retries (count > 0)
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results && detail.results.length > 0) {
        // Only refresh if at least one request was successfully retried
        const hasSuccessfulRetries = detail.results.some(
          r => r.success === true,
        );
        if (hasSuccessfulRetries) {
          // Check if any of the successful retries were DELETE requests for tasks
          const hasTaskDelete = detail.results.some(
            r =>
              r.success === true &&
              r.request?.method === "DELETE" &&
              r.request?.url?.includes("/tasks/"),
          );
          if (hasTaskDelete) {
            // Refresh tasks list to reflect deleted items
            fetchTasks(
              currentPage,
              searchTerm,
              projectId,
              statusFilter,
              employeeId,
              sortBy,
              sortOrder,
            );
          }
        }
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener(
      "offlineRequestsRetried",
      handleOfflineRequestsRetried,
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener(
        "offlineRequestsRetried",
        handleOfflineRequestsRetried,
      );
    };
  }, [
    setTitle,
    fetchTasks,
    currentPage,
    searchTerm,
    projectId,
    statusFilter,
    employeeId,
  ]);

  // Close dropdowns when clicking outside or when opening another dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      // Close all dropdowns if clicking outside
      const isClickInsideDropdown = event.target.closest(
        "[data-dropdown-container]",
      );
      const isClickOnDropdownToggle = event.target.closest(
        "[data-dropdown-toggle]",
      );

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setProjectDropdownOpen(false);
        setEmployeeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close other dropdowns when opening a new one
  useEffect(() => {
    if (projectDropdownOpen) {
      setEmployeeDropdownOpen(false);
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      setProjectDropdownOpen(false);
    }
  }, [employeeDropdownOpen]);

  useEffect(() => {
    fetchTasks(currentPage, searchTerm, projectId, statusFilter, employeeId, sortBy, sortOrder);
  }, [
    currentPage,
    searchTerm,
    projectId,
    statusFilter,
    employeeId,
    sortBy,
    sortOrder,
    perPage,
    fetchTasks,
  ]);

  // Consolidated and Optimized Project Dropdown Fetching
  useEffect(() => {
    if (!projectDropdownOpen) {
      lastProjectSearchRef.current = "";
      lastProjectPageRef.current = 1;
      return;
    }

    const searchChanged = lastProjectSearchRef.current !== projectSearchTerm;
    const pageChanged = lastProjectPageRef.current !== projectPage;

    // Determine if we need to fetch
    // We always fetch on open, or if search/page changed
    if (
      !searchChanged &&
      !pageChanged &&
      lastProjectSearchRef.current === projectSearchTerm &&
      lastProjectPageRef.current === projectPage &&
      projectOptions.length > 0
    ) {
      return;
    }

    const delay = searchChanged ? 400 : 0;
    const timeoutId = setTimeout(() => {
      fetchProjects(projectPage, projectSearchTerm);
      lastProjectSearchRef.current = projectSearchTerm;
      lastProjectPageRef.current = projectPage;
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [
    projectDropdownOpen,
    projectPage,
    projectSearchTerm,
    fetchProjects,
    projectOptions.length,
  ]);

  // Consolidated and Optimized Employee Dropdown Fetching
  useEffect(() => {
    if (!employeeDropdownOpen) {
      lastEmployeeSearchRef.current = "";
      lastEmployeePageRef.current = 1;
      return;
    }

    const searchChanged = lastEmployeeSearchRef.current !== employeeSearchTerm;
    const pageChanged = lastEmployeePageRef.current !== employeePage;

    if (
      !searchChanged &&
      !pageChanged &&
      lastEmployeeSearchRef.current === employeeSearchTerm &&
      lastEmployeePageRef.current === employeePage &&
      employeeOptions.length > 0
    ) {
      return;
    }

    const delay = searchChanged ? 400 : 0;
    const timeoutId = setTimeout(() => {
      fetchEmployees(employeePage, employeeSearchTerm);
      lastEmployeeSearchRef.current = employeeSearchTerm;
      lastEmployeePageRef.current = employeePage;
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [
    employeeDropdownOpen,
    employeePage,
    employeeSearchTerm,
    fetchEmployees,
    employeeOptions.length,
  ]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm("");
    setProjectId("");
    setStatusFilter("");
    setEmployeeId("");
    setSortBy("");
    setSortOrder("desc");
    setCurrentPage(1);
    setProjectSearchTerm("");
    setProjectPage(1);
    setEmployeeSearchTerm("");
    setEmployeePage(1);
  };

  const handleSort = column => {
    if (sortBy === column) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      setCurrentPage(1);
      fetchTasks(1, searchTerm, projectId, statusFilter, employeeId, column, newOrder);
    } else {
      setSortBy(column);
      setSortOrder("asc");
      setCurrentPage(1);
      fetchTasks(1, searchTerm, projectId, statusFilter, employeeId, column, "asc");
    }
  };

  const getSortIcon = column => sortBy !== column ? " ↕" : sortOrder === "asc" ? " ↑" : " ↓";

  const handleDelete = async () => {
    if (!taskToDelete) return;
    const idToDelete = taskToDelete.id;
    setDeleteModalOpen(false);
    setTaskToDelete(null);

    // Prepare query params for cache update
    const queryParams = {
      page: currentPage,
      per_page: perPage,
      search: searchTerm || undefined,
      project_id: projectId || undefined,
      status: statusFilter || undefined,
      employee_id: employeeId || undefined,
    };
    const cacheKey = generateTasksCacheKey(queryParams);

    try {
      // Try to use offline request manager first
      const result = await makeOfflineRequest(
        `/tasks/${idToDelete}`,
        "DELETE",
        null,
      );
      // Only after success, update UI
      const updatedTasks = tasks.filter(task => task.id !== idToDelete);
      setTasks(updatedTasks);
      toast.success(t("tasks.messages.deleteSuccess"), {
        rtl: i18n.language === "ar",
      });

      // Update pagination after successful deletion
      if (pagination && pagination.total > 0) {
        setPagination({
          ...pagination,
          total: Math.max(0, pagination.total - 1),
        });
      }

      // Update IndexedDB cache after successful deletion
      await saveTasksCache(
        cacheKey,
        updatedTasks,
        pagination
          ? { ...pagination, total: Math.max(0, pagination.total - 1) }
          : null,
      );

      // Don't refresh - data is already updated
    } catch (offlineError) {
      // If request was saved offline, show appropriate message
      if (
        offlineError.message === "OFFLINE_REQUEST_SAVED" ||
        offlineError.isOffline
      ) {
        // Remove task from all cache entries optimistically
        try {
          await removeTaskFromCache(idToDelete);
        } catch (cacheError) {
          console.error("Error removing task from cache:", cacheError);
        }

        // Only after success, update UI
        const updatedTasks = tasks.filter(task => task.id !== idToDelete);
        setTasks(updatedTasks);

        // Update pagination after removal
        if (pagination && pagination.total > 0) {
          setPagination({
            ...pagination,
            total: Math.max(0, pagination.total - 1),
          });
        }

        toast.info(
          isRTL
            ? "سيتم حذف المهمة عند عودة الاتصال بالإنترنت"
            : "Task will be deleted when connection is restored",
          { rtl: isRTL, autoClose: 5000 },
        );

        // Update IndexedDB cache (task is removed from UI)
        await saveTasksCache(
          cacheKey,
          updatedTasks,
          pagination
            ? { ...pagination, total: Math.max(0, pagination.total - 1) }
            : null,
        );

        // Don't refresh - data is already updated
        return;
      }

      // If it's a client error (4xx), show error and do NOT update UI
      if (offlineError.status >= 400 && offlineError.status < 500) {
        // Special case: started task cannot be deleted
        let errorMessage =
          offlineError?.message || t("tasks.messages.deleteError");
        // If API returns a specific message for started tasks, use it
        if (
          offlineError?.response?.data?.message &&
          (offlineError?.response?.data?.message.includes(
            "لا يمكن حذف مهمه تم البدء فيها",
          ) ||
            offlineError?.response?.data?.message.includes(
              "Cannot delete a started task",
            ))
        ) {
          errorMessage = isRTL
            ? "لا يمكن حذف مهمه تم البدء فيها"
            : "Cannot delete a started task";
        }
        toast.error(errorMessage, { rtl: isRTL });
        // Do NOT update UI (do not remove from list)
        return;
      }

      // Only use Redux dispatch as fallback for network/server errors (5xx)
      try {
        await dispatch(deleteTask(idToDelete)).unwrap();
        // Only after success, update UI
        const updatedTasks = tasks.filter(task => task.id !== idToDelete);
        setTasks(updatedTasks);
        toast.success(t("tasks.messages.deleteSuccess"), {
          rtl: i18n.language === "ar",
        });

        // Update pagination after successful deletion
        if (pagination && pagination.total > 0) {
          setPagination({
            ...pagination,
            total: Math.max(0, pagination.total - 1),
          });
        }

        // Update IndexedDB cache after successful deletion
        await saveTasksCache(
          cacheKey,
          updatedTasks,
          pagination
            ? { ...pagination, total: Math.max(0, pagination.total - 1) }
            : null,
        );

        // Don't refresh - data is already updated
      } catch (reduxError) {
        // If Redux also fails, show error and do NOT update UI
        let errorMessage =
          reduxError?.message || t("tasks.messages.deleteError");
        if (
          reduxError?.response?.data?.message &&
          (reduxError?.response?.data?.message.includes(
            "لا يمكن حذف مهمه تم البدء فيها",
          ) ||
            reduxError?.response?.data?.message.includes(
              "Cannot delete a started task",
            ))
        ) {
          errorMessage = isRTL
            ? "لا يمكن حذف مهمه تم البدء فيها"
            : "Cannot delete a started task";
        }
        toast.error(errorMessage, { rtl: isRTL });
        return;
      }
    }
  };

  const handleOpenProgressModal = task => {
    setTaskForProgress(task);
    setProgressValue(task.progress || "0");
    setProgressModalOpen(true);
  };

  const handleUpdateProgress = async () => {
    if (!taskForProgress) return;
    const progress = parseInt(progressValue, 10);
    if (isNaN(progress) || progress < 0 || progress > 100) {
      toast.error(t("tasks.progressModal.progressError"), {
        rtl: i18n.language === "ar",
      });
      return;
    }
    try {
      setIsUpdatingProgress(true);
      await dispatch(
        updateTaskProgress({ taskId: taskForProgress.id, progress }),
      ).unwrap();
      toast.success(t("tasks.messages.updateProgressSuccess"), {
        rtl: i18n.language === "ar",
      });

      // Update local state optimistically
      const updatedTasks = tasks.map(task =>
        task.id === taskForProgress.id ? { ...task, progress } : task,
      );
      setTasks(updatedTasks);

      setProgressModalOpen(false);
      setTaskForProgress(null);
      setProgressValue("");

      // Update IndexedDB cache
      const queryParams = {
        page: currentPage,
        per_page: perPage,
        search: searchTerm || undefined,
        project_id: projectId || undefined,
        status: statusFilter || undefined,
        employee_id: employeeId || undefined,
      };
      const cacheKey = generateTasksCacheKey(queryParams);
      await saveTasksCache(cacheKey, updatedTasks, pagination);

      // Refresh tasks list to get updated data from API (if online)
      if (isOnline()) {
        fetchTasks(currentPage);
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message ||
            error?.response?.data?.message ||
            t("tasks.messages.updateProgressError");
      toast.error(errorMessage, { rtl: i18n.language === "ar" });
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  const formatDate = value => {
    if (!value) return "—";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(
        i18n.language === "ar" ? "en-US" : "en-US",
      );
    } catch {
      return value;
    }
  };

  const getStatusLabel = status => {
    const statusMap = {
      pending: t("tasks.statuses.pending"),
      in_progress: t("tasks.statuses.in_progress"),
      completed: t("tasks.statuses.completed"),
      on_hold: t("tasks.statuses.on_hold"),
      cancelled: t("tasks.statuses.cancelled"),
    };
    return statusMap[status] || status;
  };

  const getPriorityLabel = priority => {
    const priorityMap = {
      low: t("tasks.priorities.low"),
      medium: t("tasks.priorities.medium"),
      high: t("tasks.priorities.high"),
    };
    return priorityMap[priority] || priority;
  };

  // Handle preview task PDF
  const handlePreviewTask = task => {
    setPreviewTask(task);
    setIsPreviewOpen(true);
    setPreviewZoom(1.0); // Reset zoom when opening preview
    setPreviewPosition({ x: 0, y: 0 }); // Reset position
  };

  // Handle preview all tasks
  const handlePreviewAll = () => {
    if (!tasks || tasks.length === 0) {
      toast.error(
        t("tasks.preview.noData", { defaultValue: "لا توجد بيانات للمعاينة" }),
        { rtl: isRTL },
      );
      return;
    }
    setPreviewTask({ all: true, tasks: tasks }); // Use special object to indicate "all"
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  // Handle mouse down for dragging
  const handleMouseDown = e => {
    if (e.button !== 0) return; // Only left mouse button
    setIsDragging(true);
    setDragStart({
      x: e.clientX - previewPosition.x,
      y: e.clientY - previewPosition.y,
    });
  };

  // Handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Handle reset to fit (original state with scroll)
  const handleResetPreview = () => {
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle print task from preview
  const handlePrintFromPreview = async () => {
    if (!previewTask) return;
    if (previewTask.all) {
      await handlePrintAll();
    } else {
      await handlePrintTask(previewTask);
    }
  };

  // Handle print single task
  const handlePrintTask = async task => {
    try {
      // Check if task exists in current tasks list, if not try to get from cache
      let taskToPrint = task;
      let usingCachedData = false;

      if (!tasks.find(t => t.id === task.id)) {
        // Task not in current list, try to get from cache
        const queryParams = {
          page: currentPage,
          per_page: perPage,
          search: searchTerm || undefined,
          project_id: projectId || undefined,
          status: statusFilter || undefined,
          employee_id: employeeId || undefined,
        };
        const allCachedTasks = await getAllTasksCacheForQuery(queryParams);
        const cachedTask = allCachedTasks.find(t => t.id === task.id);
        if (cachedTask) {
          taskToPrint = cachedTask;
          usingCachedData = true;
        } else if (!isOnline()) {
          toast.warning(
            isRTL
              ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة لهذه المهمة."
              : "No internet connection and no cached data available for this task.",
            { rtl: isRTL, autoClose: 3000 },
          );
          return;
        }
      }

      setTaskForPrint(taskToPrint);
      setPdfType("single");
      await new Promise(resolve => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`task-pdf-${taskToPrint.id}`);
      if (!element) {
        toast.error(
          t("tasks.print.error", { defaultValue: "خطأ في تحميل قالب الطباعة" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById(
            `task-pdf-${taskToPrint.id}`,
          );
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction =
              i18n.language === "ar" ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";

            const allElements = clonedElement.querySelectorAll("*");
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily =
                  '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = "bold";
                el.style.setProperty("-webkit-font-smoothing", "antialiased");
                el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                el.style.textRendering = "optimizeLegibility";
              }
            });
          }
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/png", 1.0);
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Tolerance: if the difference is less than 2mm, treat it as one page
      const tolerance = 2; // 2mm tolerance

      if (imgHeight <= pdfHeight + tolerance) {
        // If height is within tolerance, fit to one page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          Math.min(imgHeight, pdfHeight),
          undefined,
          "FAST",
        );
      } else {
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            pdfWidth,
            pageHeight,
            undefined,
            "FAST",
          );
          position += pdfHeight;
        }
      }

      const identifier =
        i18n.language === "ar"
          ? taskToPrint.name_ar || taskToPrint.name_en
          : taskToPrint.name_en || taskToPrint.name_ar || taskToPrint.id;
      pdf.save(
        `ACE_Task_${identifier}_${new Date().toISOString().split("T")[0]}.pdf`,
      );

      const printMessage = usingCachedData
        ? isRTL
          ? "تم إنشاء ملف PDF من البيانات المحفوظة محلياً"
          : "PDF generated from locally saved data"
        : t("tasks.print.success", { defaultValue: "تم إنشاء ملف PDF بنجاح" });
      toast.success(printMessage, { rtl: isRTL });
    } catch (err) {
      console.error("Task PDF error:", err);
      toast.error(
        t("tasks.print.error", { defaultValue: "حدث خطأ أثناء إنشاء PDF" }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setTaskForPrint(null);
      setPdfType(null);
    }
  };

  // Handle print all tasks
  const handlePrintAll = async () => {
    let originalTasks = null;
    let allTasksList = [];
    let usingCachedData = false;

    try {
      setIsGeneratingPdf(true);

      const queryParams = {
        page: 1,
        per_page: perPage,
        search: searchTerm || undefined,
        project_id: projectId || undefined,
        status: statusFilter || undefined,
        employee_id: employeeId || undefined,
      };

      // Try to get all tasks from cache first
      const allCachedTasks = await getAllTasksCacheForQuery(queryParams);

      if (isOnline()) {
        try {
          // Fetch all tasks with per_page = total
          const total = pagination?.total || 0;
          if (total === 0 && allCachedTasks.length === 0) {
            toast.error(
              t("tasks.print.noData", {
                defaultValue: "لا توجد بيانات للطباعة",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            return;
          }

          const params = {
            page: 1,
            per_page: total || allCachedTasks.length || 1000,
            search: searchTerm || undefined,
            project_id: projectId || undefined,
            status: statusFilter || undefined,
            employee_id: employeeId || undefined,
          };

          const payload = await dispatch(getTasks(params)).unwrap();
          const { items: fetchedTasks } = normalizeListResponse(payload);

          if (fetchedTasks && fetchedTasks.length > 0) {
            allTasksList = fetchedTasks;
          } else if (allCachedTasks.length > 0) {
            allTasksList = allCachedTasks;
            usingCachedData = true;
          }
        } catch (apiError) {
          // API failed, use cached data if available
          if (allCachedTasks.length > 0) {
            allTasksList = allCachedTasks;
            usingCachedData = true;
            toast.warning(
              isRTL
                ? "لا يمكن الاتصال بالخادم. يتم استخدام البيانات المحفوظة محلياً للطباعة."
                : "Cannot connect to server. Using locally saved data for printing.",
              { rtl: isRTL, autoClose: 3000 },
            );
          } else {
            toast.error(
              t("tasks.print.noData", {
                defaultValue: "لا توجد بيانات للطباعة",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            return;
          }
        }
      } else {
        // Offline: use cached data
        if (allCachedTasks.length > 0) {
          allTasksList = allCachedTasks;
          usingCachedData = true;
          toast.info(
            isRTL
              ? "لا يوجد اتصال بالإنترنت. يتم استخدام البيانات المحفوظة محلياً للطباعة."
              : "No internet connection. Using locally saved data for printing.",
            { rtl: isRTL, autoClose: 3000 },
          );
        } else {
          toast.error(
            isRTL
              ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة للطباعة."
              : "No internet connection and no cached data available for printing.",
            { rtl: isRTL },
          );
          setIsGeneratingPdf(false);
          return;
        }
      }

      if (!allTasksList || allTasksList.length === 0) {
        toast.error(
          t("tasks.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      // Temporarily set all tasks for PDF generation
      originalTasks = tasks;

      // Split data into chunks of 18 items per page
      const chunks = chunkArray(allTasksList, 18);
      setPdfChunks(chunks);
      setTasks(chunks[0] || []); // Set first chunk for initial render

      setPdfType("all");
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 200));

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Wait for DOM update
        await new Promise(resolve => requestAnimationFrame(resolve));

        const element = document.getElementById(`tasks-pdf-all-${chunkIndex}`);
        if (!element) {
          if (chunkIndex === 0) {
            setTasks(originalTasks);
            setPdfChunks([]);
            toast.error(
              t("tasks.print.error", {
                defaultValue: "قالب الطباعة غير متوفر",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `tasks-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction =
                i18n.language === "ar" ? "rtl" : "ltr";
              clonedElement.style.setProperty(
                "-webkit-font-smoothing",
                "antialiased",
              );
              clonedElement.style.setProperty(
                "-moz-osx-font-smoothing",
                "grayscale",
              );
              clonedElement.style.textRendering = "optimizeLegibility";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        // Tolerance: if the difference is less than 2mm, treat it as one page
        const tolerance = 2; // 2mm tolerance

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add the image to the current page - use tolerance to avoid extra empty pages
        if (imgHeight <= pdfHeight + tolerance) {
          pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            pdfWidth,
            Math.min(imgHeight, pdfHeight),
            undefined,
            "FAST",
          );
        } else {
          let position = 0;
          while (position < imgHeight) {
            if (position > 0) {
              pdf.addPage();
            }
            const remainingHeight = imgHeight - position;
            const pageHeight = Math.min(remainingHeight, pdfHeight);
            pdf.addImage(
              imgData,
              "PNG",
              0,
              0,
              pdfWidth,
              pageHeight,
              undefined,
              "FAST",
            );
            position += pdfHeight;
          }
        }
      }

      pdf.save(`ACE_Tasks_${new Date().toISOString().split("T")[0]}.pdf`);

      const printMessage = usingCachedData
        ? isRTL
          ? "تم إنشاء ملف PDF من البيانات المحفوظة محلياً"
          : "PDF generated from locally saved data"
        : t("tasks.print.success", { defaultValue: "تم تنزيل PDF بنجاح" });
      toast.success(printMessage, { rtl: isRTL });

      // Restore original tasks
      setTasks(originalTasks);
      setPdfChunks([]);
    } catch (error) {
      console.error("Tasks PDF error:", error);
      toast.error(
        t("tasks.print.error", {
          defaultValue: "حدث خطأ أثناء إنشاء ملف المهام",
        }),
        { rtl: isRTL },
      );
      // Restore original tasks on error
      if (originalTasks) {
        setTasks(originalTasks);
      }
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single task to Excel
  const handleExportTaskToExcel = task => {
    const taskName =
      i18n.language === "ar"
        ? task.name_ar || task.name_en
        : task.name_en || task.name_ar;
    const projectName = task.project_name
      ? task.project_name
      : task.project
        ? i18n.language === "ar"
          ? task.project.name_ar || task.project.name_en
          : task.project.name_en || task.project.name_ar
        : "—";
    const employeeName = task.employee?.name || "—";
    const sheetName = t("tasks.export.sheetNameSingle", {
      defaultValue: "Task",
    });
    const filePrefix = t("tasks.export.filePrefixSingle", {
      defaultValue: "Task",
    });

    const data = [
      {
        [t("tasks.tableHeaders.taskName", { defaultValue: "اسم المهمة" })]:
          taskName || "—",
        [t("tasks.tableHeaders.project", { defaultValue: "المشروع" })]:
          projectName || "—",
        [t("tasks.add.employee", { defaultValue: "الموظف المسؤول" })]:
          employeeName,
        [t("tasks.tableHeaders.status", { defaultValue: "الحالة" })]:
          getStatusLabel(task.status),
        [t("tasks.tableHeaders.priority", { defaultValue: "الأولوية" })]:
          getPriorityLabel(task.priority),
        [t("tasks.tableHeaders.startDate", { defaultValue: "تاريخ البدء" })]:
          formatDate(task.start_date) || "—",
        [t("tasks.tableHeaders.endDate", { defaultValue: "تاريخ الانتهاء" })]:
          formatDate(task.end_date) || "—",
        [t("tasks.tableHeaders.progress", { defaultValue: "التقدم" })]:
          `${task.progress || 0}%`,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const identifier = taskName || task.id;
    const fileName = `${filePrefix}_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("tasks.export.success", { defaultValue: "تم تصدير البيانات بنجاح" }),
      { rtl: isRTL },
    );
  };

  // Handle export all tasks to Excel
  const handleExportAllToExcel = () => {
    if (!tasks || tasks.length === 0) {
      toast.error(
        t("tasks.export.noData", { defaultValue: "لا توجد بيانات للتصدير" }),
        { rtl: isRTL },
      );
      return;
    }

    const sheetName = t("tasks.export.sheetNameAll", {
      defaultValue: "Tasks",
    });
    const filePrefix = t("tasks.export.filePrefixAll", {
      defaultValue: "Tasks",
    });

    const data = tasks.map((task, index) => {
      const taskName =
        i18n.language === "ar"
          ? task.name_ar || task.name_en
          : task.name_en || task.name_ar;
      const projectName = task.project_name
        ? task.project_name
        : task.project
          ? i18n.language === "ar"
            ? task.project.name_ar || task.project.name_en
            : task.project.name_en || task.project.name_ar
          : "—";
      const employeeName = task.employee?.name || "—";

      return {
        [t("tasks.tableHeaders.serial", { defaultValue: "م" })]:
          (paginationInfo.current_page - 1) * perPage + (index + 1),
        [t("tasks.tableHeaders.taskName", { defaultValue: "اسم المهمة" })]:
          taskName || "—",
        [t("tasks.tableHeaders.project", { defaultValue: "المشروع" })]:
          projectName || "—",
        [t("tasks.add.employee", { defaultValue: "الموظف المسؤول" })]:
          employeeName,
        [t("tasks.tableHeaders.status", { defaultValue: "الحالة" })]:
          getStatusLabel(task.status),
        [t("tasks.tableHeaders.priority", { defaultValue: "الأولوية" })]:
          getPriorityLabel(task.priority),
        [t("tasks.tableHeaders.startDate", { defaultValue: "تاريخ البدء" })]:
          formatDate(task.start_date) || "—",
        [t("tasks.tableHeaders.endDate", { defaultValue: "تاريخ الانتهاء" })]:
          formatDate(task.end_date) || "—",
        [t("tasks.tableHeaders.progress", { defaultValue: "التقدم" })]:
          `${task.progress || 0}%`,
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fileName = `${filePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("tasks.export.success", { defaultValue: "تم تصدير البيانات بنجاح" }),
      { rtl: isRTL },
    );
  };

  return (
    <div
      style={{
        padding: 30,
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20 }}>
          {t("tasks.pageTitle", { defaultValue: "Tasks" })}
        </h1>
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ color: "white", fontSize: 14, whiteSpace: "nowrap" }}
            >
              {t("common.perPage", { defaultValue: "Per Page" })}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => {
                  const newValue = Math.max(1, perPageInput - 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={pagination?.total || tasks.length || 1000}
                value={perPageInput}
                onChange={e => {
                  const value = parseInt(e.target.value, 10) || 1;
                  const maxValue = pagination?.total || tasks.length || 1000;
                  setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                }}
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() => {
                  const maxValue = pagination?.total || tasks.length || 1000;
                  const newValue = Math.min(maxValue, perPageInput + 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.apply", { defaultValue: "تطبيق" })}
            </button>
          </div>
        </div>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12 }}
      >
        <div style={{ position: "relative", flex: "1 1 320px" }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#888",
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t("tasks.searchPlaceholder")}
            style={{
              width: "100%",
              padding: isRTL ? "12px 42px 12px 16px" : "12px 16px 12px 42px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: 8,
              color: "white",
            }}
          />
        </div>
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setProjectDropdownOpen(prev => !prev)}
            style={{
              ...dropdownDisplayStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <span>
              {projectId
                ? (isRTL
                    ? projectOptions.find(
                        p => String(p.id) === String(projectId),
                      )?.name_ar ||
                      projectOptions.find(
                        p => String(p.id) === String(projectId),
                      )?.name_en
                    : projectOptions.find(
                        p => String(p.id) === String(projectId),
                      )?.name_en ||
                      projectOptions.find(
                        p => String(p.id) === String(projectId),
                      )?.name_ar) || `${t("tasks.project")} #${projectId}`
                : t("tasks.selectProject")}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={projectDropdownOpen}
            isLoading={isProjectsLoading}
            searchTerm={projectSearchTerm}
            onSearchChange={val => {
              setProjectSearchTerm(val);
              setProjectPage(1);
            }}
            searchInputRef={projectSearchInputRef}
            items={projectOptions}
            pagination={projectPagination}
            onPageChange={setProjectPage}
            onClose={() => setProjectDropdownOpen(false)}
            onSelect={item => {
              setProjectId(String(item.id));
              setProjectDropdownOpen(false);
              setProjectSearchTerm("");
              setProjectPage(1);
              setCurrentPage(1);
            }}
            label={t("tasks.project")}
          />
        </div>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 }}
      >
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setEmployeeDropdownOpen(prev => !prev)}
            style={{
              ...dropdownDisplayStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <span>
              {employeeId
                ? (isRTL
                    ? employeeOptions.find(
                        e => String(e.id) === String(employeeId),
                      )?.name_ar ||
                      employeeOptions.find(
                        e => String(e.id) === String(employeeId),
                      )?.name_en
                    : employeeOptions.find(
                        e => String(e.id) === String(employeeId),
                      )?.name_en ||
                      employeeOptions.find(
                        e => String(e.id) === String(employeeId),
                      )?.name_ar) || `${t("tasks.add.employee")} #${employeeId}`
                : t("tasks.add.selectEmployee")}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={employeeDropdownOpen}
            isLoading={isEmployeesLoading}
            searchTerm={employeeSearchTerm}
            onSearchChange={val => {
              setEmployeeSearchTerm(val);
              setEmployeePage(1);
            }}
            searchInputRef={employeeSearchInputRef}
            items={employeeOptions}
            pagination={employeePagination}
            onPageChange={setEmployeePage}
            onClose={() => setEmployeeDropdownOpen(false)}
            onSelect={item => {
              setEmployeeId(String(item.id));
              setEmployeeDropdownOpen(false);
              setEmployeeSearchTerm("");
              setEmployeePage(1);
              setCurrentPage(1);
            }}
            label={t("tasks.add.employee")}
          />
        </div>
        <div style={{ flex: "1 1 320px" }}>
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, width: "100%" }}
          >
            <option value="">{t("tasks.allStatuses")}</option>
            <option value="pending">{t("tasks.statuses.pending")}</option>
            <option value="in_progress">
              {t("tasks.statuses.in_progress")}
            </option>
            <option value="completed">{t("tasks.statuses.completed")}</option>
            <option value="on_hold">{t("tasks.statuses.on_hold")}</option>
            <option value="cancelled">{t("tasks.statuses.cancelled")}</option>
          </select>
        </div>
      </div>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handleReset}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            width: "auto",
          }}
        >
          {t("tasks.reset")}
        </button>
        <button
          onClick={() => navigate("/tasks/add")}
          style={{
            backgroundColor: "#AC2000",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
          }}
        >
          <FaPlus /> {t("tasks.newTask")}
        </button>
        <button
          onClick={handlePreviewAll}
          style={{
            backgroundColor: "#444",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
          }}
        >
          <FaEye /> {t("tasks.preview.all", { defaultValue: "عرض PDF للكل" })}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdf}
          style={{
            backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
            opacity: isGeneratingPdf ? 0.6 : 1,
          }}
        >
          <FaPrint />{" "}
          {isGeneratingPdf
            ? t("tasks.print.generating", { defaultValue: "جاري الطباعة..." })
            : t("tasks.print.all", { defaultValue: "طباعة الكل" })}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
          }}
        >
          <FaFileExcel />{" "}
          {t("tasks.export.all", { defaultValue: "تصدير Excel الكل" })}
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 1400,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{ padding: 14, textAlign: "center", color: "white", fontWeight: "bold", fontSize: 14, borderBottom: "1px solid #333", cursor: "pointer", width: 60 }}
                  onClick={() => handleSort("id")}
                >
                  #{getSortIcon("id")}
                </th>
                {[
                  { key: "name_ar", label: t("tasks.tableHeaders.taskName") },
                  { key: "project_id", label: t("tasks.tableHeaders.project") },
                  { key: "employee_id", label: t("tasks.add.employee") },
                  { key: "status", label: t("tasks.tableHeaders.status") },
                  { key: "priority", label: t("tasks.tableHeaders.priority") },
                  { key: "start_date", label: t("tasks.tableHeaders.startDate") },
                  { key: "end_date", label: t("tasks.tableHeaders.endDate") },
                  { key: null, label: t("tasks.tableHeaders.attachments") },
                  { key: "progress", label: t("tasks.tableHeaders.progress") },
                  { key: null, label: t("tasks.tableHeaders.actions") },
                ].map(col => (
                  <th
                    key={col.label}
                    onClick={col.key ? () => handleSort(col.key) : undefined}
                    style={{
                      padding: 14,
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 14,
                      borderBottom: "1px solid #333",
                      cursor: col.key ? "pointer" : "default",
                    }}
                  >
                    {col.label}{col.key ? getSortIcon(col.key) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{ textAlign: "center", padding: 20, color: "#ccc" }}
                  >
                    {t("tasks.loading")}
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{ textAlign: "center", padding: 20, color: "#ccc" }}
                  >
                    {t("tasks.noData")}
                  </td>
                </tr>
              ) : (
                tasks.map((task, index) => (
                  <tr
                    key={task.id || index}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td style={cellStyle}>
                      {(paginationInfo.current_page - 1) * perPage + index + 1}
                    </td>
                    <td style={cellStyle}>
                      {i18n.language === "ar"
                        ? task.name_ar || task.name_en || "—"
                        : task.name_en || task.name_ar || "—"}
                    </td>
                    <td style={cellStyle}>
                      {task.project_name
                        ? i18n.language === "ar"
                          ? task.project_name
                          : task.project_name
                        : task.project
                          ? i18n.language === "ar"
                            ? task.project.name_ar ||
                              task.project.name_en ||
                              "—"
                            : task.project.name_en ||
                              task.project.name_ar ||
                              "—"
                          : task.project_id
                            ? `Project #${task.project_id}`
                            : "—"}
                    </td>
                    <td style={cellStyle}>
                      {task.employee?.name ||
                        task.assigned_user?.name ||
                        (i18n.language === "ar"
                          ? task.employee?.name_ar || task.employee?.name_en
                          : task.employee?.name_en || task.employee?.name_ar) ||
                        "—"}
                    </td>
                    <td style={cellStyle}>{getStatusLabel(task.status)}</td>
                    <td style={cellStyle}>{getPriorityLabel(task.priority)}</td>
                    <td style={cellStyle}>{formatDate(task.start_date)}</td>
                    <td style={cellStyle}>{formatDate(task.end_date)}</td>
                    <td style={{ ...cellStyle, minWidth: 150 }}>
                      {(() => {
                        const attachments = getTaskAttachments(task);
                        if (!attachments || attachments.length === 0)
                          return t("tasks.attachments.none", {
                            defaultValue: "لا توجد",
                          });
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              setAttachmentsForModal(attachments);
                              setAttachmentsTaskName(
                                i18n.language === "ar"
                                  ? task.name_ar || task.name_en || ""
                                  : task.name_en || task.name_ar || "",
                              );
                              setAttachmentsModalOpen(true);
                            }}
                            style={{
                              backgroundColor: "#1f5ff1",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 12,
                              width: "100%",
                            }}
                          >
                            {t("tasks.attachments.viewDetails", {
                              count: attachments.length,
                              defaultValue: "عرض التفاصيل ({{count}})",
                            })}
                          </button>
                        );
                      })()}
                    </td>
                    <td style={cellStyle}>
                      {task.progress ? `${task.progress}%` : "—"}
                    </td>
                    <td style={{ ...cellStyle, minWidth: 200 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 8,
                          flexWrap: "nowrap",
                        }}
                      >
                        <ActionButton
                          title={t("tasks.actions.changeProgress")}
                          bg="#3498db"
                          onClick={() => handleOpenProgressModal(task)}
                        >
                          <FaChartLine />
                        </ActionButton>
                        <ActionButton
                          title={t("tasks.actions.view", {
                            defaultValue: "عرض",
                          })}
                          onClick={() => handlePreviewTask(task)}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t("tasks.print.single", {
                            defaultValue: "طباعة",
                          })}
                          bg={isGeneratingPdf ? "#666" : "#17a2b8"}
                          onClick={() => handlePrintTask(task)}
                          disabled={isGeneratingPdf}
                          style={{
                            opacity: isGeneratingPdf ? 0.6 : 1,
                            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                          }}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t("tasks.export.single", {
                            defaultValue: "تصدير Excel",
                          })}
                          bg="#28a745"
                          onClick={() => handleExportTaskToExcel(task)}
                        >
                          <FaFileExcel />
                        </ActionButton>
                        <ActionButton
                          title={t("tasks.actions.edit")}
                          onClick={() => navigate(`/tasks/edit/${task.id}`)}
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t("tasks.actions.delete")}
                          bg="#c0392b"
                          onClick={() => {
                            setTaskToDelete(task);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            padding: 16,
            borderTop: "1px solid #333",
          }}
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={paginationInfo.current_page === 1}
            style={paginationButton(paginationInfo.current_page === 1)}
          >
            {t("tasks.pagination.previous")}
          </button>
          <span style={{ padding: 8 }}>
            {paginationInfo.current_page} / {paginationInfo.last_page}
          </span>
          <button
            onClick={() =>
              setCurrentPage(prev =>
                prev >= paginationInfo.last_page
                  ? paginationInfo.last_page
                  : prev + 1,
              )
            }
            disabled={paginationInfo.current_page >= paginationInfo.last_page}
            style={paginationButton(
              paginationInfo.current_page >= paginationInfo.last_page,
            )}
          >
            {t("tasks.pagination.next")}
          </button>
        </div>
      </div>
      {deleteModalOpen && (
        <div style={modalOverlayStyle}>
          <div
            style={{
              ...modalCardStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>
              {t("tasks.deleteModal.title")}
            </h3>
            <p style={{ marginBottom: 24 }}>{t("tasks.deleteModal.message")}</p>
            <div
              style={{
                display: "flex",
                justifyContent: isRTL ? "flex-end" : "flex-start",
                gap: 12,
                flexDirection: isRTL ? "row" : "row-reverse",
              }}
            >
              <button
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  setDeleteModalOpen(false);
                  setTaskToDelete(null);
                }}
              >
                {t("tasks.deleteModal.cancel")}
              </button>
              <button style={modalPrimaryButtonStyle} onClick={handleDelete}>
                {t("tasks.deleteModal.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {progressModalOpen && taskForProgress && (
        <div
          style={modalOverlayStyle}
          onClick={() => setProgressModalOpen(false)}
        >
          <div
            style={{
              ...modalCardStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16, color: "white" }}>
              {t("tasks.progressModal.title")}
            </h3>
            <p style={{ marginBottom: 16, color: "#ccc" }}>
              {t("tasks.progressModal.task")}:{" "}
              {(isRTL
                ? taskForProgress.name_ar || taskForProgress.name_en
                : taskForProgress.name_en || taskForProgress.name_ar) ||
                `#${taskForProgress.id}`}
            </p>
            <div style={{ marginBottom: 24 }}>
              <label
                style={{ display: "flex", flexDirection: "column", gap: 6 }}
              >
                <span style={{ fontSize: 14, color: "#ccc" }}>
                  {t("tasks.progressModal.progress")}
                </span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={progressValue}
                  onChange={e => setProgressValue(e.target.value)}
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    border: "1px solid #333",
                    backgroundColor: "#1a1f2e",
                    color: "white",
                    fontSize: 16,
                    direction: isRTL ? "rtl" : "ltr",
                    textAlign: isRTL ? "right" : "left",
                  }}
                  autoFocus
                />
              </label>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: isRTL ? "flex-end" : "flex-start",
                gap: 12,
                flexDirection: isRTL ? "row" : "row-reverse",
              }}
            >
              <button
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  setProgressModalOpen(false);
                  setTaskForProgress(null);
                  setProgressValue("");
                }}
                disabled={isUpdatingProgress}
              >
                {t("tasks.progressModal.cancel")}
              </button>
              <button
                style={modalPrimaryButtonStyle}
                onClick={handleUpdateProgress}
                disabled={isUpdatingProgress}
              >
                {isUpdatingProgress
                  ? t("tasks.progressModal.saving")
                  : t("tasks.progressModal.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {attachmentsModalOpen && (
        <div
          style={modalOverlayStyle}
          onClick={() => setAttachmentsModalOpen(false)}
        >
          <div
            style={{
              ...modalCardStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
              maxWidth: 700,
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>
              {t("tasks.attachments.label")}
            </h3>
            <p style={{ marginTop: 0, marginBottom: 16, color: "#ccc" }}>
              {attachmentsTaskName || ""}
            </p>
            {attachmentsForModal.length === 0 ? (
              <div style={{ color: "#ccc" }}>
                {t("tasks.attachments.none", {
                  defaultValue: "لا توجد مرفقات",
                })}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  maxHeight: 320,
                  overflowY: "auto",
                }}
              >
                {attachmentsForModal.map((file, idx) => {
                  const name =
                    file.name ||
                    file.original_name ||
                    file.file_name ||
                    file.title ||
                    t("tasks.attachments.fallbackFileName", {
                      index: idx + 1,
                      defaultValue: `ملف ${idx + 1}`,
                    });
                  const url =
                    file.url || file.path || file.file_path || file.file;
                  return (
                    <div
                      key={file.id || idx}
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #333",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          color: "white",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {name}
                      </span>
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "#1f9dff",
                            textDecoration: "none",
                            fontWeight: "bold",
                            fontSize: 12,
                          }}
                        >
                          {t("tasks.attachments.viewDownload", {
                            defaultValue: "عرض / تحميل",
                          })}
                        </a>
                      ) : (
                        <span style={{ color: "#ccc", fontSize: 12 }}>
                          {t("tasks.attachments.noLink", {
                            defaultValue: "لا يوجد رابط",
                          })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: isRTL ? "flex-end" : "flex-start",
                marginTop: 16,
              }}
            >
              <button
                style={modalPrimaryButtonStyle}
                onClick={() => setAttachmentsModalOpen(false)}
              >
                {t("common.close", { defaultValue: "إغلاق" })}
              </button>
            </div>
          </div>
        </div>
      )}
      <PrintTemplates
        taskForPrint={taskForPrint}
        pdfType={pdfType}
        tasks={tasks}
        paginationInfo={paginationInfo}
        i18n={i18n}
        t={t}
        formatDate={formatDate}
        getStatusLabel={getStatusLabel}
        getPriorityLabel={getPriorityLabel}
        publicUrl={publicUrl}
        headerLogo={headerLogo}
        pdfChunks={pdfChunks}
        searchTerm={searchTerm}
        projectId={projectId}
        statusFilter={statusFilter}
        employeeId={employeeId}
        projectOptions={projectOptions}
        employeeOptions={employeeOptions}
        perPage={perPage}
      />

      {/* PDF Preview Modal */}
      {isPreviewOpen &&
        previewTask &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              padding: "20px",
              overflow: "hidden",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setIsPreviewOpen(false);
                setPreviewTask(null);
              }
            }}
            onWheel={e => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setPreviewZoom(prev =>
                  Math.max(0.3, Math.min(2, prev + delta)),
                );
              }
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                borderRadius: "12px",
                padding: "20px",
                width: "100%",
                height: "100%",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexShrink: 0,
                }}
              >
                <h2 style={{ color: "white", margin: 0, fontSize: "20px" }}>
                  {t("tasks.preview.title", { defaultValue: "معاينة المهمة" })}
                </h2>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      marginRight: "10px",
                    }}
                  >
                    <button
                      onClick={() =>
                        setPreviewZoom(prev => Math.max(0.3, prev - 0.1))
                      }
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                      title={t("tasks.preview.zoomOut", {
                        defaultValue: "تصغير",
                      })}
                    >
                      −
                    </button>
                    <span
                      style={{
                        color: "white",
                        minWidth: "60px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {Math.round(previewZoom * 100)}%
                    </span>
                    <button
                      onClick={() =>
                        setPreviewZoom(prev => Math.min(2, prev + 0.1))
                      }
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                      title={t("tasks.preview.zoomIn", {
                        defaultValue: "تكبير",
                      })}
                    >
                      +
                    </button>
                    <button
                      onClick={handleResetPreview}
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                      title={t("tasks.preview.fitToScreen", {
                        defaultValue: "ملاءمة الشاشة",
                      })}
                    >
                      {t("tasks.preview.fit", { defaultValue: "ملاءمة" })}
                    </button>
                  </div>
                  <button
                    onClick={handlePrintFromPreview}
                    disabled={isGeneratingPdf}
                    style={{
                      backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: isGeneratingPdf ? 0.6 : 1,
                    }}
                  >
                    <FaPrint />{" "}
                    {t("tasks.print.single", { defaultValue: "طباعة" })}
                  </button>
                  <button
                    onClick={() => {
                      setIsPreviewOpen(false);
                      setPreviewTask(null);
                    }}
                    style={{
                      backgroundColor: "#666",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      cursor: "pointer",
                    }}
                  >
                    {t("common.close", { defaultValue: "إغلاق" })}
                  </button>
                </div>
              </div>
              <div
                ref={previewContainerRef}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  padding: "20px",
                  overflow: previewZoom <= 1.0 ? "auto" : "hidden",
                  flex: 1,
                  display: "flex",
                  alignItems: previewZoom <= 1.0 ? "flex-start" : "center",
                  justifyContent: previewZoom <= 1.0 ? "flex-start" : "center",
                  minHeight: 0,
                  position: "relative",
                  cursor:
                    previewZoom > 1.0 && isDragging
                      ? "grabbing"
                      : previewZoom > 1.0
                        ? "grab"
                        : "default",
                  margin: "20px",
                }}
                onMouseDown={previewZoom > 1.0 ? handleMouseDown : undefined}
              >
                <div
                  style={{
                    transform:
                      previewZoom > 1.0
                        ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                        : `scale(${previewZoom})`,
                    transformOrigin:
                      previewZoom <= 1.0 ? "top center" : "center center",
                    transition: isDragging ? "none" : "transform 0.2s ease",
                    width: previewZoom <= 1.0 ? "100%" : "794px",
                    height: "1123px",
                    userSelect: previewZoom > 1.0 ? "none" : "auto",
                    marginLeft: previewZoom <= 1.0 ? "0" : "0",
                  }}
                >
                  {previewTask?.all ? (
                    <AllTasksPreviewTemplate
                      tasks={previewTask.tasks}
                      paginationInfo={paginationInfo}
                      i18n={i18n}
                      t={t}
                      formatDate={formatDate}
                      getStatusLabel={getStatusLabel}
                      getPriorityLabel={getPriorityLabel}
                      publicUrl={publicUrl}
                      headerLogo={headerLogo}
                      searchTerm={searchTerm}
                      projectId={projectId}
                      statusFilter={statusFilter}
                      employeeId={employeeId}
                      projectOptions={projectOptions}
                      employeeOptions={employeeOptions}
                    />
                  ) : (
                    <TaskPreviewTemplate
                      task={previewTask}
                      i18n={i18n}
                      t={t}
                      formatDate={formatDate}
                      getStatusLabel={getStatusLabel}
                      getPriorityLabel={getPriorityLabel}
                      publicUrl={publicUrl}
                      headerLogo={headerLogo}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const cellStyle = {
  padding: 14,
  textAlign: "center",
  color: "white",
  fontSize: 14,
};

const paginationButton = disabled => ({
  padding: "8px 16px",
  backgroundColor: disabled ? "#333" : "#AC2000",
  color: "white",
  border: "none",
  borderRadius: 6,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const ActionButton = ({
  children,
  onClick,
  title,
  bg = "#2f3644",
  disabled,
  style: customStyle,
}) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      backgroundColor: bg,
      color: "white",
      border: "none",
      borderRadius: 6,
      padding: "8px 10px",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 38,
      opacity: disabled ? 0.6 : 1,
      ...customStyle,
    }}
  >
    {children}
  </button>
);

const filterInputStyle = {
  width: "100%",
  padding: 12,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  color: "white",
};

const dropdownDisplayStyle = {
  width: "100%",
  padding: "12px 14px",
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  color: "white",
  textAlign: "right",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
};

const dropdownSearchStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 6,
  border: "1px solid #333",
  backgroundColor: "#14263b",
  color: "white",
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: "center",
  color: "#ccc",
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div
      style={{ ...dropdownMenuStyle, direction: isRTL ? "rtl" : "ltr" }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t("tasks.dropdown.searchPlaceholder")} ${label}`}
          value={searchTerm}
          onChange={e => {
            onSearchChange(e.target.value);
          }}
          onFocus={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          autoFocus
          style={{
            ...dropdownSearchStyle,
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        />
      </div>
      {pagination &&
        (pagination.last_page > 1 ||
          (pagination.last_page === 1 && pagination.current_page === 1)) && (
          <div
            style={{
              padding: "8px",
              borderTop: "1px solid #333",
              background: "#14263b",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  color: "#ccc",
                  fontSize: 11,
                  direction: isRTL ? "rtl" : "ltr",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("tasks.dropdown.page")} {pagination.current_page}{" "}
                {t("tasks.dropdown.of")} {pagination.last_page}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page > 1) onPageChange(1);
                }}
                disabled={pagination.current_page === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: pagination.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ««
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page > 1)
                    onPageChange(pagination.current_page - 1);
                }}
                disabled={pagination.current_page === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: pagination.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ‹
              </button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1)
                  start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(page => (
                  <button
                    key={page}
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onPageChange(page);
                    }}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: page === current ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border:
                        page === current
                          ? "1px solid #AC2000"
                          : "1px solid #444",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: page === current ? "bold" : "normal",
                      minWidth: "28px",
                    }}
                  >
                    {page}
                  </button>
                ));
              })()}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page < pagination.last_page)
                    onPageChange(pagination.current_page + 1);
                }}
                disabled={pagination.current_page === pagination.last_page}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === pagination.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    pagination.current_page === pagination.last_page
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === pagination.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ›
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page < pagination.last_page)
                    onPageChange(pagination.last_page);
                }}
                disabled={pagination.current_page === pagination.last_page}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === pagination.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    pagination.current_page === pagination.last_page
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === pagination.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                »»
              </button>
            </div>
          </div>
        )}
      <div style={{ maxHeight: 260, overflowY: "auto", position: "relative" }}>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(26, 31, 46, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 5,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                border: "3px solid #AC2000",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            ></div>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        {(!Array.isArray(items) ||
          (Array.isArray(items) ? items : []).length === 0) &&
        !isLoading ? (
          <div
            style={{
              ...dropdownEmptyStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("tasks.dropdown.noResults")}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map(item => (
            <div
              key={item.id}
              style={{
                padding: "10px 12px",
                borderTop: "1px solid #333",
                cursor: "pointer",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item, i18n.language)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatEntity = (entity, language = "ar") => {
  if (!entity) return "—";
  const code = entity.code ? `${entity.code} - ` : "";
  const isRTL = language === "ar";
  const name = isRTL
    ? entity.name_ar || entity.name_en || ""
    : entity.name_en || entity.name_ar || "";
  return `${code}${name}` || `#${entity.id}`;
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
  padding: 16,
};

const modalCardStyle = {
  backgroundColor: "#1b3350",
  borderRadius: 12,
  padding: 24,
  border: "1px solid #333",
  width: "100%",
  maxWidth: 420,
};

const modalPrimaryButtonStyle = {
  backgroundColor: "#AC2000",
  color: "white",
  border: "none",
  borderRadius: 8,
  padding: "10px 18px",
  cursor: "pointer",
};

const modalSecondaryButtonStyle = {
  backgroundColor: "transparent",
  color: "white",
  border: "1px solid #444",
  borderRadius: 8,
  padding: "10px 18px",
  cursor: "pointer",
};

// Print Templates Component
const PrintTemplates = ({
  taskForPrint,
  pdfType,
  tasks,
  paginationInfo,
  i18n,
  t,
  formatDate,
  getStatusLabel,
  getPriorityLabel,
  publicUrl,
  headerLogo,
  pdfChunks,
  searchTerm,
  projectId,
  statusFilter,
  employeeId,
  projectOptions,
  employeeOptions,
  perPage,
}) => {
  const isRTL = i18n.language === "ar";

  return (
    <>
      {/* Single Task Print Template */}
      {taskForPrint && pdfType === "single" && (
        <div
          id={`task-pdf-${taskForPrint.id}`}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "794px",
            height: "1123px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              height: "1043px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />
                {/* Contract Agreement Header with Project Name */}
                {(() => {
                  const taskName = isRTL
                    ? taskForPrint.name_ar ||
                      taskForPrint.name_en ||
                      taskForPrint.task_name ||
                      "—"
                    : taskForPrint.name_en ||
                      taskForPrint.name_ar ||
                      taskForPrint.task_name ||
                      "—";
                  return (
                    <div
                      style={{
                        textAlign: "center",
                        marginBottom: "12px",
                        padding: "8px 12px",
                        direction: isRTL ? "rtl" : "ltr",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: "bold",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "18px",
                          color: "#000000",
                          fontWeight: "bold",
                        }}
                      >
                        {isRTL ? "مهمة" : "Task"}
                        {taskName && taskName !== "—" ? `: ${taskName}` : ""}
                      </span>
                    </div>
                  );
                })()}

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      border: "2px solid #000",
                      borderRadius: "4px",
                      overflow: "hidden",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {(() => {
                      const taskName = isRTL
                        ? taskForPrint.name_ar || taskForPrint.name_en
                        : taskForPrint.name_en || taskForPrint.name_ar;
                      const projectName = taskForPrint.project_name
                        ? taskForPrint.project_name
                        : taskForPrint.project
                          ? isRTL
                            ? taskForPrint.project.name_ar ||
                              taskForPrint.project.name_en
                            : taskForPrint.project.name_en ||
                              taskForPrint.project.name_ar
                          : "—";
                      const employeeName = taskForPrint.employee?.name || "—";

                      return [
                        {
                          label: t("tasks.tableHeaders.taskName", {
                            defaultValue: "اسم المهمة",
                          }),
                          value: taskName || "—",
                        },
                        {
                          label: t("tasks.tableHeaders.project", {
                            defaultValue: "المشروع",
                          }),
                          value: projectName || "—",
                        },
                        {
                          label: t("tasks.add.employee", {
                            defaultValue: "الموظف المسؤول",
                          }),
                          value: employeeName,
                        },
                        {
                          label: t("tasks.tableHeaders.status", {
                            defaultValue: "الحالة",
                          }),
                          value: getStatusLabel(taskForPrint.status),
                          accent: true,
                        },
                        {
                          label: t("tasks.tableHeaders.priority", {
                            defaultValue: "الأولوية",
                          }),
                          value: getPriorityLabel(taskForPrint.priority),
                        },
                        {
                          label: t("tasks.tableHeaders.startDate", {
                            defaultValue: "تاريخ البدء",
                          }),
                          value: formatDate(taskForPrint.start_date) || "—",
                        },
                        {
                          label: t("tasks.tableHeaders.endDate", {
                            defaultValue: "تاريخ الانتهاء",
                          }),
                          value: formatDate(taskForPrint.end_date) || "—",
                        },
                        {
                          label: t("tasks.tableHeaders.progress", {
                            defaultValue: "التقدم",
                          }),
                          value: `${taskForPrint.progress || 0}%`,
                          accent: true,
                        },
                      ]
                        .filter(row => row.value && row.value !== "—")
                        .map(row => (
                          <div
                            key={row.label}
                            style={{
                              display: "flex",
                              borderBottom: "1px solid #000",
                              minHeight: "38px",
                            }}
                          >
                            <div
                              style={{
                                width: "30%",
                                borderLeft: "1px solid #000",
                                backgroundColor: "rgba(227, 242, 253, 0.4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isRTL
                                  ? "flex-start"
                                  : "flex-start",
                                textAlign: isRTL ? "right" : "left",
                                fontWeight: "700",
                                fontSize: "14px",
                                direction: isRTL ? "rtl" : "ltr",
                                color: "#1976d2",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                                padding: "0 10px",
                              }}
                            >
                              {row.label}
                            </div>
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isRTL
                                  ? "flex-start"
                                  : "flex-start",
                                textAlign: isRTL ? "right" : "left",
                                fontSize: "15px",
                                fontWeight: row.accent ? "700" : "500",
                                color: row.accent ? "#0057c2" : "#000",
                                direction: isRTL ? "rtl" : "ltr",
                                padding: "0 10px",
                                backgroundColor: "rgba(255, 255, 255, 0.3)",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                              }}
                            >
                              {row.value}
                            </div>
                          </div>
                        ));
                    })()}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Tasks Print Template */}
      {pdfType === "all" &&
        Array.isArray(pdfChunks) &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`tasks-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: isRTL ? "rtl" : "ltr",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />
                  {/* Contract Agreement Header */}
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "4px",
                      padding: "8px 12px 0",
                      direction: isRTL ? "rtl" : "ltr",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "18px",
                        color: "#000080",
                        fontWeight: "bold",
                      }}
                    >
                      {isRTL ? "المهام" : "Tasks"}
                    </span>
                  </div>
                  <p style={{ textAlign: "center", fontSize: "12px", margin: "0 0 4px 0", color: "#333", fontFamily: '"Times New Roman", serif' }}>
                    {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                  {(searchTerm || projectId || statusFilter || employeeId) && (
                    <div style={{ textAlign: "center", fontSize: "11px", marginBottom: "6px", color: "#555", direction: isRTL ? "rtl" : "ltr", fontFamily: '"Times New Roman", serif' }}>
                      {[
                        searchTerm && `${t("tasks.searchPlaceholder", { defaultValue: "بحث" })}: ${searchTerm}`,
                        projectId && projectOptions.find(p => String(p.id) === String(projectId)) && `${t("tasks.tableHeaders.project", { defaultValue: "المشروع" })}: ${isRTL ? (projectOptions.find(p => String(p.id) === String(projectId))?.name_ar || projectOptions.find(p => String(p.id) === String(projectId))?.name_en) : (projectOptions.find(p => String(p.id) === String(projectId))?.name_en || projectOptions.find(p => String(p.id) === String(projectId))?.name_ar)}`,
                        statusFilter && `${t("tasks.tableHeaders.status", { defaultValue: "الحالة" })}: ${getStatusLabel(statusFilter)}`,
                        employeeId && employeeOptions.find(e => String(e.id) === String(employeeId)) && `${t("tasks.add.employee", { defaultValue: "الموظف" })}: ${employeeOptions.find(e => String(e.id) === String(employeeId))?.name || ""}`,
                      ].filter(Boolean).join(" | ")}
                    </div>
                  )}

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                      paddingBottom: "120px",
                    }}
                  >
                    <div
                      style={{
                        marginTop: "8px",
                        direction: isRTL ? "rtl" : "ltr",
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "11px",
                          border: "1px solid #000",
                          fontFamily: '"Times New Roman", "Times", serif',
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center", fontWeight: "700", backgroundColor: "rgba(238, 242, 247, 0.7)", width: "36px" }}>#</th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.taskName", {
                                defaultValue: "اسم المهمة",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.project", {
                                defaultValue: "المشروع",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.add.employee", {
                                defaultValue: "الموظف المسؤول",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.status", {
                                defaultValue: "الحالة",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.priority", {
                                defaultValue: "الأولوية",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.startDate", {
                                defaultValue: "تاريخ البدء",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.endDate", {
                                defaultValue: "تاريخ الانتهاء",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("tasks.tableHeaders.progress", {
                                defaultValue: "التقدم",
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((task, idx) => {
                            const taskName = isRTL
                              ? task.name_ar || task.name_en
                              : task.name_en || task.name_ar;
                            const projectName = task.project_name
                              ? task.project_name
                              : task.project
                                ? isRTL
                                  ? task.project.name_ar || task.project.name_en
                                  : task.project.name_en || task.project.name_ar
                                : "—";
                            const employeeName = task.employee?.name || "—";

                            return (
                              <tr
                                key={task.id}
                                style={{
                                  backgroundColor:
                                    idx % 2 === 0
                                      ? "rgba(255, 255, 255, 0.7)"
                                      : "rgba(255, 255, 255, 0.5)",
                                }}
                              >
                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center", backgroundColor: idx % 2 === 0 ? "rgba(255, 255, 255, 0.7)" : "rgba(255, 255, 255, 0.5)", width: "36px" }}>{chunkIndex * perPage + idx + 1}</td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: isRTL ? "right" : "left",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                    wordBreak: "break-all",
                                    maxWidth: "120px",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {taskName || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: isRTL ? "right" : "left",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                    wordBreak: "break-all",
                                    maxWidth: "120px",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {projectName || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: isRTL ? "right" : "left",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                    wordBreak: "break-all",
                                    maxWidth: "100px",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {employeeName}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {getStatusLabel(task.status)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {getPriorityLabel(task.priority)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {formatDate(task.start_date) || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {formatDate(task.end_date) || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {task.progress ? `${task.progress}%` : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
    </>
  );
};

// Preview Template Component (visible version of print template)
const TaskPreviewTemplate = ({
  task,
  i18n,
  t,
  formatDate,
  getStatusLabel,
  getPriorityLabel,
  publicUrl,
  headerLogo,
}) => {
  const isRTL = i18n.language === "ar";

  if (!task) return null;

  return (
    <div
      style={{
        width: "794px",
        height: "1123px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          height: "1043px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            height: "1033px",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />
            {/* Contract Agreement Header with Project Name */}
            {(() => {
              const taskname = isRTL
                ? task.name_ar || task.name_en || task.task_name || "—"
                : task.name_en || task.name_ar || task.task_name || "—";
              return (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "12px",
                    padding: "8px 12px",
                    direction: isRTL ? "rtl" : "ltr",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                  }}
                >
                  <span
                    style={{
                      fontSize: "18px",
                      color: "#000000",
                      fontWeight: "bold",
                    }}
                  >
                    {isRTL ? "مهمة" : "Task"}
                    {taskname && taskname !== "—" ? `: ${taskname}` : ""}
                  </span>
                </div>
              );
            })()}

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
                paddingBottom: "120px",
              }}
            >
              <div
                style={{
                  marginTop: "12px",
                  border: "2px solid #000",
                  borderRadius: "4px",
                  overflow: "hidden",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                }}
              >
                {(() => {
                  const taskName = isRTL
                    ? task.name_ar || task.name_en
                    : task.name_en || task.name_ar;
                  const projectName = task.project_name
                    ? task.project_name
                    : task.project
                      ? isRTL
                        ? task.project.name_ar || task.project.name_en
                        : task.project.name_en || task.project.name_ar
                      : "—";
                  const employeeName =
                    task.employee?.name || task.employee_name || "—";

                  return [
                    {
                      label: t("tasks.tableHeaders.taskName", {
                        defaultValue: "اسم المهمة",
                      }),
                      value: taskName || "—",
                    },
                    {
                      label: t("tasks.tableHeaders.project", {
                        defaultValue: "المشروع",
                      }),
                      value: projectName || "—",
                    },
                    {
                      label: t("tasks.add.employee", {
                        defaultValue: "الموظف المسؤول",
                      }),
                      value: employeeName,
                    },
                    {
                      label: t("tasks.tableHeaders.status", {
                        defaultValue: "الحالة",
                      }),
                      value: getStatusLabel(task.status),
                      accent: true,
                    },
                    {
                      label: t("tasks.tableHeaders.priority", {
                        defaultValue: "الأولوية",
                      }),
                      value: getPriorityLabel(task.priority),
                    },
                    {
                      label: t("tasks.tableHeaders.startDate", {
                        defaultValue: "تاريخ البدء",
                      }),
                      value: formatDate(task.start_date) || "—",
                    },
                    {
                      label: t("tasks.tableHeaders.endDate", {
                        defaultValue: "تاريخ الانتهاء",
                      }),
                      value: formatDate(task.end_date) || "—",
                    },
                    {
                      label: t("tasks.tableHeaders.progress", {
                        defaultValue: "التقدم",
                      }),
                      value: `${task.progress || 0}%`,
                      accent: true,
                    },
                  ]
                    .filter(row => row.value && row.value !== "—")
                    .map(row => (
                      <div
                        key={row.label}
                        style={{
                          display: "flex",
                          borderBottom: "1px solid #000",
                          minHeight: "38px",
                        }}
                      >
                        <div
                          style={{
                            width: "30%",
                            borderLeft: "1px solid #000",
                            backgroundColor: "rgba(227, 242, 253, 0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: isRTL ? "flex-start" : "flex-start",
                            textAlign: isRTL ? "right" : "left",
                            fontWeight: "700",
                            fontSize: "14px",
                            direction: isRTL ? "rtl" : "ltr",
                            color: "#1976d2",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                            padding: "0 10px",
                          }}
                        >
                          {row.label}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: isRTL ? "flex-start" : "flex-start",
                            textAlign: isRTL ? "right" : "left",
                            fontSize: "15px",
                            fontWeight: row.accent ? "700" : "500",
                            color: row.accent ? "#0057c2" : "#000",
                            direction: isRTL ? "rtl" : "ltr",
                            padding: "0 10px",
                            backgroundColor: "rgba(255, 255, 255, 0.3)",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                          }}
                        >
                          {row.value}
                        </div>
                      </div>
                    ));
                })()}
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "18px",
                right: "18px",
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// All Tasks Preview Template Component
const AllTasksPreviewTemplate = ({
  tasks,
  paginationInfo,
  i18n,
  t,
  formatDate,
  getStatusLabel,
  getPriorityLabel,
  publicUrl,
  headerLogo,
  searchTerm,
  projectId,
  statusFilter,
  employeeId,
  projectOptions,
  employeeOptions,
}) => {
  const isRTL = i18n.language === "ar";

  if (!tasks || tasks.length === 0) return null;

  return (
    <div
      style={{
        width: "794px",
        height: "1123px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          height: "1043px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            height: "1033px",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
              flex: 1,
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />
            {/* Contract Agreement Header */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "4px",
                padding: "8px 12px 0",
                direction: isRTL ? "rtl" : "ltr",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
              }}
            >
              <span
                style={{
                  fontSize: "18px",
                  color: "#000080",
                  fontWeight: "bold",
                }}
              >
                {isRTL ? "المهام" : "Tasks"}
              </span>
            </div>
            <p style={{ textAlign: "center", fontSize: "12px", margin: "0 0 4px 0", color: "#333", fontFamily: '"Times New Roman", serif' }}>
              {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
            {(searchTerm || projectId || statusFilter || employeeId) && (
              <div style={{ textAlign: "center", fontSize: "11px", marginBottom: "6px", color: "#555", direction: isRTL ? "rtl" : "ltr", fontFamily: '"Times New Roman", serif' }}>
                {[
                  searchTerm && `${t("tasks.searchPlaceholder", { defaultValue: "بحث" })}: ${searchTerm}`,
                  projectId && projectOptions.find(p => String(p.id) === String(projectId)) && `${t("tasks.tableHeaders.project", { defaultValue: "المشروع" })}: ${isRTL ? (projectOptions.find(p => String(p.id) === String(projectId))?.name_ar || projectOptions.find(p => String(p.id) === String(projectId))?.name_en) : (projectOptions.find(p => String(p.id) === String(projectId))?.name_en || projectOptions.find(p => String(p.id) === String(projectId))?.name_ar)}`,
                  statusFilter && `${t("tasks.tableHeaders.status", { defaultValue: "الحالة" })}: ${getStatusLabel(statusFilter)}`,
                  employeeId && employeeOptions.find(e => String(e.id) === String(employeeId)) && `${t("tasks.add.employee", { defaultValue: "الموظف" })}: ${employeeOptions.find(e => String(e.id) === String(employeeId))?.name || ""}`,
                ].filter(Boolean).join(" | ")}
              </div>
            )}

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  marginTop: "8px",
                  direction: isRTL ? "rtl" : "ltr",
                  fontFamily: '"Times New Roman", "Times", serif',
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    border: "1px solid #000",
                    fontFamily: '"Times New Roman", "Times", serif',
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
                      <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center", fontWeight: "700", backgroundColor: "rgba(238, 242, 247, 0.7)", width: "36px" }}>#</th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.taskName", {
                          defaultValue: "اسم المهمة",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.project", {
                          defaultValue: "المشروع",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.add.employee", {
                          defaultValue: "الموظف المسؤول",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.status", {
                          defaultValue: "الحالة",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.priority", {
                          defaultValue: "الأولوية",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.progress", {
                          defaultValue: "التقدم",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.startDate", {
                          defaultValue: "تاريخ البدء",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("tasks.tableHeaders.endDate", {
                          defaultValue: "تاريخ الانتهاء",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task, index) => {
                      const taskName = isRTL
                        ? task.name_ar || task.name_en
                        : task.name_en || task.name_ar;
                      const projectName = task.project_name
                        ? task.project_name
                        : task.project
                          ? isRTL
                            ? task.project.name_ar || task.project.name_en
                            : task.project.name_en || task.project.name_ar
                          : "—";
                      const employeeName =
                        task.employee?.name || task.employee_name || "—";

                      return (
                        <tr
                          key={task.id}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                        >
                          <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center", backgroundColor: "rgba(255, 255, 255, 0.7)", width: "36px" }}>{index + 1}</td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                              wordBreak: "break-all",
                              maxWidth: "120px",
                              whiteSpace: "normal",
                            }}
                          >
                            {taskName || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                              wordBreak: "break-all",
                              maxWidth: "120px",
                              whiteSpace: "normal",
                            }}
                          >
                            {projectName || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                              wordBreak: "break-all",
                              maxWidth: "100px",
                              whiteSpace: "normal",
                            }}
                          >
                            {employeeName}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {getStatusLabel(task.status)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {getPriorityLabel(task.priority)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {task.progress || 0}%
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatDate(task.start_date) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatDate(task.end_date) || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              style={{
                marginTop: "auto",
                paddingTop: "20px",
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
